import { UserBasicInfo, UserQueryDto } from '@/service/user/user-types';
import {
  HeardFrom,
  SocialPlatform,
  TrialInfo,
  UserData,
  UserSubscription,
} from '@/types/database-types';
import client, { routes } from '@/utils/axios';

export class UserService {
  static async getMe(): Promise<UserData> {
    const data = await client
      .get<UserData>(routes.user.getMe)
      .then((res) => res.data);
    return data;
  }

  static async getSubscription(): Promise<UserSubscription | null> {
    const data = (await client.get(routes.user.getSubscription)).data;
    if (!data) return null;
    return {
      ...data,
      startedOn: new Date(data.startedOn),
      currentPeriodStart: new Date(data.currentPeriodStart),
      expiresOn: new Date(data.expiresOn),
    };
  }

  /// Supprime définitivement le compte.
  ///
  /// Renvoie le magasin où un abonnement reste à résilier, le cas échéant :
  /// ni Apple ni Google ne laissent notre serveur le faire, et l'utilisateur
  /// continuerait d'être prélevé sans le savoir.
  static async deleteAccount(args: {
    password?: string;
  }): Promise<{ storeSubscriptionToCancel: string | null }> {
    return (
      await client.delete(routes.user.deleteAccount, { data: args })
    ).data;
  }

  static async getTrialStatus(): Promise<TrialInfo> {
    return (await client.get<TrialInfo>(routes.user.trial)).data;
  }

  static async activateTrial(): Promise<TrialInfo> {
    return (await client.post<TrialInfo>(routes.user.trial)).data;
  }

  static async updateUser(args: {
    username?: string;
    private?: boolean;
    bio?: string;
    socialLinks?: Partial<Record<SocialPlatform, string>>;
    strategies?: string[];
  }): Promise<UserData> {
    return (
      await client.patch(routes.user.update, {
        username: args.username,
        private: args.private,
        bio: args.bio,
        socialLinks: args.socialLinks,
        strategies: args.strategies,
      })
    ).data;
  }

  static async uploadAvatar({ file }: { file: File }): Promise<UserData> {
    const formData = new FormData();

    formData.append('file', file);

    return (
      await client.post(routes.user.avatar, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      })
    ).data;
  }

  static async deleteAvatar(): Promise<UserData> {
    return (await client.delete(routes.user.avatar)).data;
  }

  static async uploadBanner({ file }: { file: File }): Promise<UserData> {
    const formData = new FormData();
    formData.append('file', file);
    return (
      await client.post(routes.user.banner, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      })
    ).data;
  }

  static async deleteBanner(): Promise<UserData> {
    return (await client.delete(routes.user.banner)).data;
  }

  static async updatePassword({
    password,
    oldPassword,
  }: {
    password: string;
    oldPassword: string;
  }): Promise<void> {
    await client.patch(routes.user.updatePassword, { password, oldPassword });
  }

  static async startChangeEmail({ email }: { email: string }): Promise<void> {
    await client.post(routes.user.changeEmail, { email });
  }

  static async confirmChangeEmail({ code }: { code: string }): Promise<void> {
    await client.post(routes.user.confirmChangeEmail, { code });
  }

  static async completeOnboarding(): Promise<UserData> {
    return (await client.patch(routes.user.completeOnboarding)).data;
  }

  static async setHeardFrom(heardFrom: HeardFrom): Promise<UserData> {
    return (await client.patch(routes.user.heardFrom, { heardFrom })).data;
  }

  /// Administrateur : rejoue l'accueil d'une première connexion.
  static async replayOnboarding(): Promise<UserData> {
    return (await client.patch(routes.user.replayOnboarding)).data;
  }

  static async search(username: string): Promise<UserQueryDto> {
    const res = await client.get<UserQueryDto>(routes.user.search, {
      params: {
        username,
      },
    });
    return res.data;
  }

  static async getPublicUser(userId: string): Promise<UserBasicInfo> {
    const res = await client.get<UserBasicInfo>(
      routes.user.getPublicUser(userId),
    );
    return res.data;
  }
}
